import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaLeaf, FaShieldAlt, FaHeart, FaArrowLeft, FaCheck, FaInfoCircle, FaClipboardList } from 'react-icons/fa';
import { IconType } from 'react-icons/lib';
import { getProductById } from '../api/products';
import type { Product } from '../api/types';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const BackButton = styled.button`
  display: none;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.theme.colors.primary};
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const SidebarBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  width: 100%;
  border: 2px solid ${props => props.theme.colors.primary};
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: 600;
  transition: all ${props => props.theme.transitions.fast};
  margin-bottom: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}CC 0%, ${props => props.theme.colors.accent}CC 100%);
  color: white;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/img/Background_Pig.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.25;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroInfo = styled.div``;

const ProductCategory = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  border: 1px solid rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;
  margin-bottom: ${props => props.theme.spacing.md};
  margin-right: ${props => props.theme.spacing.xs};
  display: inline-block;
  word-break: keep-all;
`;

const ProductTitle = styled(motion.h1)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 3rem;
  font-weight: 300;
  margin-bottom: ${props => props.theme.spacing.lg};
  word-break: keep-all;
  overflow-wrap: break-word;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const ProductSubtitle = styled(motion.p)`
  font-size: 1.3rem;
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
  word-break: keep-all;
  overflow-wrap: break-word;
  white-space: pre-line;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
`;

const FeatureTag = styled.span`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const HeroImage = styled.div<{ bgColor: string }>`
  width: 46%; /* 원래 70%의 66% */
  height: auto;
  aspect-ratio: 248 / 277; /* 실제 로고 이미지 비율 (248x277) */
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8rem;
  color: rgba(255, 255, 255, 0.9);
  box-shadow: ${props => props.theme.shadows.xl};
  padding: ${props => props.theme.spacing.xs}; /* 아주 살짝 여백 */
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 53%; /* 원래 80%의 66% */
    padding: ${props => props.theme.spacing.xs};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 59%; /* 원래 90%의 66% */
  }
`;

const ProductIcon = styled.div<{ $bgColor: string }>`
  width: 100%;
  height: 100%;
  background-color: ${props => props.$bgColor};
  mask-image: url('/img/product_Icon.png');
  mask-size: contain; /* 컨테이너에 딱 맞게 */
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url('/img/product_Icon.png');
  -webkit-mask-size: contain; /* 컨테이너에 딱 맞게 */
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15)); /* 아이콘 그림자 */
`;

const ContentSection = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div``;

const ContentCard = styled.div`
  background: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const ContentText = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.8;
  margin-bottom: ${props => props.theme.spacing.md};
  word-break: keep-all;
  overflow-wrap: break-word;
  white-space: pre-line;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} 0;
  color: ${props => props.theme.colors.textLight};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  word-break: keep-all;
  overflow-wrap: break-word;

  &:last-child {
    border-bottom: none;
  }
`;

const SpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SpecRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SpecLabel = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  background: ${props => props.theme.colors.background};
  width: 40%;
`;

const SpecValue = styled.td`
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.textLight};
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  text-align: center;
`;

const ContactButton = styled.button`
  background: white;
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-weight: 600;
  margin-top: ${props => props.theme.spacing.md};
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #e74c3c;
  font-size: 1.1rem;
`;

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Type cast icons
  const LeafIcon = FaLeaf as IconType;
  const ShieldIcon = FaShieldAlt as IconType;
  const HeartIcon = FaHeart as IconType;
  const ArrowLeftIcon = FaArrowLeft as IconType;
  const CheckIcon = FaCheck as IconType;
  const InfoIcon = FaInfoCircle as IconType;
  const ClipboardIcon = FaClipboardList as IconType;

  // API에서 제품 상세 데이터 가져오기
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError(t('productDetail.error.noId'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await getProductById(parseInt(id), i18n.language);
        setProduct(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('productDetail.error.loadFailed'));
        console.error('Failed to fetch product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, i18n.language, t]);

  // 카테고리 번역 함수
  const translateCategory = (category: string): string => {
    const categoryKey = `products.categoryMap.${category}` as const;
    return t(categoryKey, category); // 번역이 없으면 원본 반환
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>{t('productDetail.loading')}</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button onClick={() => navigate('/products')}>{t('productDetail.backToProducts')}</button>
        </div>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>{t('productDetail.error.notFound')}</h2>
          <button onClick={() => navigate('/products')}>{t('productDetail.backToProducts')}</button>
        </div>
      </Container>
    );
  }


  return (
    <Container>
      <BackButton onClick={() => navigate('/products')}>
        <ArrowLeftIcon />
        {t('productDetail.backToProducts')}
      </BackButton>

      <HeroSection>
        <HeroContent>
          <HeroInfo>
            {product.category.split(',').map((cat, idx) => (
              <ProductCategory key={idx}>{translateCategory(cat.trim())}</ProductCategory>
            ))}
            <ProductTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {product.name}
            </ProductTitle>
            {product.subtitle && (
              <ProductSubtitle
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {product.subtitle}
              </ProductSubtitle>
            )}
            <FeaturesList>
              {(product.features || []).map((feature, idx) => (
                <FeatureTag key={idx}>
                  {idx === 0 && <LeafIcon />}
                  {idx === 1 && <ShieldIcon />}
                  {idx === 2 && <HeartIcon />}
                  {idx === 3 && <LeafIcon />}
                  {feature}
                </FeatureTag>
              ))}
            </FeaturesList>
          </HeroInfo>
          <HeroImage bgColor={product.bgColor || '#6B8E23'}>
            <ProductIcon $bgColor={product.bgColor || '#6B8E23'} />
          </HeroImage>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentGrid>
          <MainContent>
            {/* 제품 설명 */}
            <ContentCard>
              <SectionTitle>
                <InfoIcon />
                {t('productDetail.detailedDescription')}
              </SectionTitle>
              <ContentText>{product.description}</ContentText>
            </ContentCard>

            {/* 효과 및 장점 */}
            <ContentCard>
              <SectionTitle>
                <CheckIcon />
                {t('productDetail.mainBenefits')}
              </SectionTitle>
              <BenefitsList>
                {product.benefits && product.benefits.map((benefit, index) => (
                  <BenefitItem key={index}>
                    <CheckIcon color="#4CAF50" />
                    {benefit}
                  </BenefitItem>
                ))}
              </BenefitsList>
            </ContentCard>

            {/* 제품 규격 */}
            {product.specifications && (
              <ContentCard>
                <SectionTitle>
                  <ClipboardIcon />
                  {t('productDetail.specificationsTitle')}
                </SectionTitle>
                <SpecTable>
                  <tbody>
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <SpecRow key={key}>
                        <SpecLabel>{key}</SpecLabel>
                        <SpecValue>{value}</SpecValue>
                      </SpecRow>
                    ))}
                  </tbody>
                </SpecTable>
              </ContentCard>
            )}
          </MainContent>

          <Sidebar>
            <SidebarBackButton onClick={() => navigate('/products')}>
              <ArrowLeftIcon />
              {t('productDetail.backToProducts')}
            </SidebarBackButton>
            <ContentCard>
              <SectionTitle>
                {t('productDetail.mainFeatures')}
              </SectionTitle>
              <FeaturesList>
                {(product.features || []).map((feature, idx) => (
                  <FeatureTag key={idx} style={{ background: '#f5f5f5', color: '#333' }}>
                    {idx === 0 && <LeafIcon color="#4CAF50" />}
                    {idx === 1 && <ShieldIcon color="#2196F3" />}
                    {idx === 2 && <HeartIcon color="#FF5722" />}
                    {idx === 3 && <LeafIcon color="#4CAF50" />}
                    {feature}
                  </FeatureTag>
                ))}
              </FeaturesList>
            </ContentCard>
            <ContactCard>
              <h2>{t('productDetail.inquiry')}</h2>
              <p>{t('productDetail.inquiryDescription1')}</p>
              <p>{t('productDetail.inquiryDescription2')}</p>
              <ContactButton onClick={() => navigate('/inquiry')}>
                {t('productDetail.inquiryButton')}
              </ContactButton>
            </ContactCard>
          </Sidebar>
        </ContentGrid>
      </ContentSection>
    </Container>
  );
};

export default ProductDetailPage;
